import React, { useState } from "react";
import Pagination from "./Pagination";
import {
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/20/solid";

const PayrollTable = ({
  columns,
  data = [],
  onRowClick,
  actions = [],
  title,
  description,
  description2,
  description3,
  showPagination,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [sortState, setSortState] = useState({
    columnKey: null,
    direction: null,
  }); // State for sorting

  // Helper function to get nested property
  const getNestedProperty = (obj, path) => {
    return path
      .split(/[\.\[\]]/)
      .filter(Boolean)
      .reduce((acc, part) => acc && acc[part], obj);
  };

  // Sort data based on current sortState
  const sortedData = React.useMemo(() => {
    if (!sortState.columnKey || !sortState.direction) return data;

    const sortColumn = columns.find(
      (column) => column.header === sortState.columnKey
    );

    if (!sortColumn) return data;

    const compareFn = (a, b) => {
      const aValue =
        typeof sortColumn.accessor === "function"
          ? sortColumn.accessor(a)
          : getNestedProperty(a, sortColumn.accessor);

      const bValue =
        typeof sortColumn.accessor === "function"
          ? sortColumn.accessor(b)
          : getNestedProperty(b, sortColumn.accessor);

      if (aValue === bValue) return 0;

      if (sortState.direction === "ascending") {
        return aValue > bValue ? 1 : -1;
      } else if (sortState.direction === "descending") {
        return aValue < bValue ? 1 : -1;
      }
      return 0;
    };

    return [...data].sort(compareFn);
  }, [data, sortState, columns]);

  // Handle column header click for sorting
  const handleSort = (header) => {
    setSortState((prev) => {
      if (prev.columnKey === header) {
        // Rotate through default -> ascending -> descending -> default
        if (prev.direction === "ascending")
          return { columnKey: header, direction: "descending" };
        if (prev.direction === "descending")
          return { columnKey: null, direction: null };
      }
      return { columnKey: header, direction: "ascending" };
    });
  };

  return (
    <div className="px-2 py-2">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-sm font-medium text-gray-900">{title}</h2>
          {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
        {actions.length > 0 && (
          <div className="flex gap-2">
          {actions.map((action, index) => (
            <button
              key={index}
              type="button"
              onClick={action.onClick}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-100">
              {action.label}
            </button>
          ))}
        </div>
        )}
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.header}
                        scope="col"
                  className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 cursor-pointer"
                        onClick={() => handleSort(column.header)}>
                        <div className="flex items-center">
                          {column.header}
                          <span className="ml-1">
                            {sortState.columnKey === column.header ? (
                              sortState.direction === "ascending" ? (
                          <ChevronUpIcon className="h-3 w-3 text-gray-400" />
                              ) : (
                          <ChevronDownIcon className="h-3 w-3 text-gray-400" />
                              )
                            ) : (
                        <ChevronUpDownIcon className="h-3 w-3 text-gray-400" />
                            )}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {sortedData.length === 0 && (
                    <tr>
                      <td
                  colSpan={columns.length}
                  className="px-2 py-1.5 text-xs text-gray-500 text-center">
                  No data available.
                      </td>
                    </tr>
                  )}
                  {sortedData.map((item, index) => (
                    <tr
                      key={item._id}
                      onClick={() => onRowClick(item)}
                      className={`cursor-pointer hover:bg-gray-100 ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                      }`}>
                      {columns.map((column) => (
                        <td
                          key={column.header}
                    className="whitespace-nowrap px-2 py-1.5 text-xs text-gray-600">
                          {typeof column.accessor === "function"
                            ? column.accessor(item)
                            : getNestedProperty(item, column.accessor)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
      </div>
              {showPagination && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                />
              )}
    </div>
  );
};

export default PayrollTable;
